import { colorMatrix } from "../../../maps/components/MapComponent/constants";
import { getCircleColor } from "../../../shared/layout/Plots/utils";

export interface SeedColorProduct {
  id: string;
  name: string;
  options?: number[];
  range?: { min: number; max: number };
}

/** The stable mid-tone used to identify a seed product's legacy color family. */
export const getSeedProductFamilyColor = (productIndex: number): string =>
  colorMatrix[
    ((productIndex % colorMatrix.length) + colorMatrix.length) %
      colorMatrix.length
  ]?.[50] ?? "#0092e4";

/**
 * Derive a display color without changing the saved assignment color. Rates are
 * scaled with the legacy helper using only the actual rates for this plot, so
 * the product keeps its palette family while higher rates become darker and
 * lower rates become lighter. Configured options and product ranges do not
 * affect the shade domain.
 */
export const getSeedProductColor = <T extends SeedColorProduct>(
  product: T,
  products: T[],
  rate: number | null | undefined,
  plotRates: Array<number | null | undefined> = []
): string => {
  const productIndex = products.findIndex((item) => item.id === product.id);
  if (
    productIndex < 0 ||
    typeof rate !== "number" ||
    !Number.isFinite(rate) ||
    rate <= 0
  ) {
    return "transparent";
  }

  // Keep cleared rows in the live domain, as the legacy display helper does.
  // Do not replace their rates or collapse the remaining row to a singleton.
  const rates = plotRates.filter(
    (itemRate) =>
      itemRate === null ||
      (typeof itemRate === "number" &&
        Number.isFinite(itemRate) &&
        itemRate > 0)
  );
  if (rates.length === 0) return "transparent";

  const trialProducts = products.map((item) => ({ variety: item.name }));
  const rateRows = rates.map((itemRate) => ({
    variety: product.name,
    rate: itemRate,
  }));

  return (
    getCircleColor(product.name, rate, trialProducts, undefined, rateRows) ||
    "transparent"
  );
};
